import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { parseSafeDateTime, parseTimeParts, reuploadExternalImage } from "./eventService";
import { geocodeAddress } from "../lib/helpers";

// Fluxo de publicação de eventos importados (JSON da IA), compartilhado pelo
// importador manual (AdminImportEvents) e pela aprovação da fila de
// pendentes (AdminPendingEvents).

// Mesmo fallback usado dentro de geocodeAddress() (lib/helpers.js). Só entra
// em jogo se a Cloud Function responder sem lançar erro mas sem coordenadas
// (ex.: Google Geocoding sem resultado para um endereço) — geocodeAddress()
// só cai no fallback dela quando a chamada lança exceção, então esse caso
// passa direto sem isso aqui, e addDoc() rejeita campo undefined.
const FALLBACK_COORDS = { latitude: -26.3045, longitude: -48.8487 };

// Falha ao baixar/enviar a imagem — o chamador decide se pula só esse
// evento (importador em lote) ou mostra o erro.
export class ImageImportError extends Error {
  constructor(message) {
    super(message);
    this.name = "ImageImportError";
  }
}

// 🔐 VALIDAÇÃO DO TEU PADRÃO
export const validateImportedEvent = (event) => {
  if (event.isFree && event.ticketPrice > 0) {
    throw new Error("Evento gratuito não pode ter preço");
  }

  if (!event.isFree && event.ticketPrice == 0) {
    throw new Error("Evento pago precisa ter preço");
  }
};

// Publica um evento do contrato de importação em `events` e devolve o id.
export const publishImportedEvent = async (event) => {
  // eslint-disable-next-line no-unused-vars
  const { time, endTime, ...eventWithoutTime } = event;

  // Parse with the same safe strategy used by the manual flow (no
  // native Date()-on-string ambiguity). startDate is the source of
  // truth for the event's real moment; date mirrors it exactly —
  // same pattern as the manual creation flow and the Flutter app.
  const safeStartDate = parseSafeDateTime(event.startDate || event.date);

  // endDate's real hour, in priority order:
  // 1. Explicit "endTime" field (e.g. "23:00"), same as the manual flow.
  // 2. Hour already embedded in endDate/date (e.g. "...T23:00:00").
  // 3. Fallback to end-of-day (23:59:59) ONLY when neither exists —
  //    compatibility for AI payloads that don't provide an end time yet.
  const endDateSource = event.endDate || event.date;
  const hasEmbeddedEndTime = typeof endDateSource === 'string' && /T\d{2}:\d{2}/.test(endDateSource);
  const hasEndTimeField = typeof endTime === 'string' && endTime.includes(':');

  let safeEndDate = parseSafeDateTime(endDateSource);
  if (hasEndTimeField) {
    const { hours, minutes } = parseTimeParts(endTime);
    safeEndDate = new Date(safeEndDate.getFullYear(), safeEndDate.getMonth(), safeEndDate.getDate(), hours, minutes, 0, 0);
  } else if (!hasEmbeddedEndTime) {
    safeEndDate = new Date(safeEndDate.getFullYear(), safeEndDate.getMonth(), safeEndDate.getDate(), 23, 59, 59, 0);
  }

  // Geocode address to get real coordinates (same routine used by the
  // manual create/update flow in eventService.js).
  const coords = await geocodeAddress(event.location, event.city);

  // Baixa a imagem da URL externa (Sympla, Instagram, site do
  // organizador etc.) e sobe pro Storage do projeto — igual ao fluxo
  // manual, em vez de salvar a URL de terceiro direto no Firestore.
  // O link original fica gravado em imageSourceUrl (rastreio da origem da
  // arte, ex.: para atender um pedido de remoção).
  let finalImageUrl = eventWithoutTime.imageUrl;
  const imageSourceUrl = typeof finalImageUrl === "string" && finalImageUrl.trim() ? finalImageUrl.trim() : null;
  if (imageSourceUrl) {
    try {
      finalImageUrl = await reuploadExternalImage(finalImageUrl);
    } catch (imageError) {
      throw new ImageImportError(imageError.message || "Falha ao baixar/enviar a imagem");
    }
  }

  const docRef = await addDoc(collection(db, "events"), {
    ...eventWithoutTime,

    imageUrl: finalImageUrl,
    imageSourceUrl,
    latitude: coords?.latitude ?? FALLBACK_COORDS.latitude,
    longitude: coords?.longitude ?? FALLBACK_COORDS.longitude,
    date: Timestamp.fromDate(safeStartDate),
    startDate: Timestamp.fromDate(safeStartDate),
    endDate: Timestamp.fromDate(safeEndDate),
    createdAt: Timestamp.now(),
    lastUpdated: Timestamp.now(),

    isImported: true,
  });

  return docRef.id;
};
